import { Component, Inject, OnInit, OnChanges, SimpleChanges, Input, Output, EventEmitter, Optional } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpEventType } from '@angular/common/http';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTabsModule } from '@angular/material/tabs';
import { FormsModule } from '@angular/forms';
import { Highlight } from 'ngx-highlightjs';
import { DataService } from '../../services/data.service';
import { FileUploadService } from '../../services/file-upload.service';
import { DataSourceRegister, DataSourceSchema } from '../../models/datasource';

export interface DataSourceDialogData {
  title?: string;
  projectId?: string;
  mode?: 'create' | 'edit';
  initialStage?: DialogStage;
  dataSourceData?: any; // For edit mode
  showExistingDataSources?: boolean; // Control visibility of existing data sources list
  preloadedFile?: File; // Preloaded file for direct upload
  featureContext?: 'data-profile' | 'anomaly-detection' | 'general'; // Context for which feature is using this dialog
}

export interface DataSourceFormData {
  connectionType: string;
  name?: string;
  description?: string;
  tags?: string;
  file?: File;
  contentType?: string;
  endpoint?: string;
  token?: string;
  schemaJson?: string;
}

type DialogStage = 'connection-type' | 'file-upload' | 'api-setup' | 'api-listening';

@Component({
  selector: 'app-new-data-source-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatTableModule,
    MatProgressSpinnerModule,
    MatProgressBarModule,
    MatTabsModule,
    FormsModule,
    Highlight
  ],
  templateUrl: './new-data-source-dialog.component.html',
  styleUrls: ['./new-data-source-dialog.component.scss']
})
export class NewDataSourceDialogComponent implements OnInit, OnChanges {
  // Input/Output for inline mode
  @Input() inlineData?: DataSourceDialogData;
  @Output() inlineSubmit = new EventEmitter<{ data: DataSourceFormData }>();
  @Output() inlineCancel = new EventEmitter<void>();
  @Output() titleChange = new EventEmitter<string>();
  @Output() subtitleChange = new EventEmitter<string>();
  @Output() dataSourceCreated = new EventEmitter<{ dataSourceId: string, dataSourceName: string, hasSchema: boolean }>();
  @Output() schemaDetected = new EventEmitter<{ dataSourceId: string }>();
  
  // Get data from either inline input or dialog injection
  get effectiveData(): DataSourceDialogData {
    return this.inlineData || this.data || {};
  }
  
  // Check if running in inline mode
  get isInlineMode(): boolean {
    return !!this.inlineData;
  }

  // Check if API Listener should be disabled based on feature context
  get isApiListenerDisabled(): boolean {
    const context = this.effectiveData.featureContext;
    return context === 'data-profile' || context === 'anomaly-detection';
  }

  // Get the reason why API Listener is disabled
  get apiListenerDisabledReason(): string {
    const context = this.effectiveData.featureContext;
    if (context === 'data-profile') {
      return 'API Listeners are not supported for Data Assessment';
    } else if (context === 'anomaly-detection') {
      return 'API Listeners are not supported for Anomaly Detection';
    }
    return '';
  }

  currentStage: DialogStage = 'connection-type';
  formData: DataSourceFormData = {
    connectionType: '',
    name: '',
    description: '',
    tags: '',
    contentType: 'JSON'
  };

  connectionTypes = [
    { 
      id: 'excel', 
      label: 'Upload File', 
      type: 'File', 
      icon: 'microsoft-excel', 
      description: 'Upload files directly from your computer.',
      idealFor: 'Stores the data in DQP for easy reuse across the application',
      features: ['Data Assessment', 'Clean Data', 'Anomaly Detection', 'Hypothesis Testing']
    },
    { 
      id: 'api-listener', 
      label: 'API Listener', 
      type: 'API', 
      icon: 'network-world', 
      description: 'Push data to your dedicated API Endpoint.',
      idealFor: 'Real-time data processing without persisting data in the system',
      features: ['Clean Data']
    }
  ];
  selectedFile: File | null = null;
  fileInfo = {
    size: '',
    rowCount: '',
    columnCount: ''
  };
  
  isCreatingApiListener = false;
  apiListenerError: string | null = null;
  isRegeneratingToken = false;
  tokenRegenerationError: string | null = null;
  tokenJustCopied = false;
  codeJustCopied = false;
  
  isUploadingFile = false;
  fileUploadError: string | null = null;
  uploadProgress = 0;
  uploadedBytes = 0;
  totalBytes = 0;
  uploadStatusText = 'Uploading file...';
  
  // Existing data sources
  existingDataSources: any[] = [];
  filteredDataSources: any[] = [];
  loadingDataSources = false;
  selectedExistingDataSource: any = null;
  searchQuery: string = '';
  
  // Schema data for API Listener
  schemaColumns: DataSourceSchema[] = [];
  loadingSchema = false;
  dataSourceId: string | null = null;
  private schemaPollingInterval: any = null;

  constructor(
    @Optional() public dialogRef: MatDialogRef<NewDataSourceDialogComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: DataSourceDialogData,
    private dataService: DataService,
    private fileUploadService: FileUploadService
  ) {}

  ngOnInit(): void {
    this.initializeComponent();
    this.loadExistingDataSources();
  }
  
  ngOnDestroy(): void {
    // Clean up polling interval
    this.stopSchemaPolling();
  }

  getConnectionTypesByCategory(category: string): any[] {
    return this.connectionTypes.filter(type => type.type === category);
  }

  private stopSchemaPolling(): void {
    if (this.schemaPollingInterval) {
      clearInterval(this.schemaPollingInterval);
      this.schemaPollingInterval = null;
    }
  }

  private startSchemaPolling(): void {
    // Stop any existing polling first
    this.stopSchemaPolling();
    
    // Only poll if we're on api-listening stage, it's an API Listener, and schema is not fully setup
    if (this.currentStage === 'api-listening' && 
        this.formData.connectionType === 'api-listener' && 
        !this.isApiListenerFullySetup()) {
      
      // Poll every 5 seconds for better responsiveness
      this.schemaPollingInterval = setInterval(() => {
        // Only load schema if we're not already waiting for a previous request
        if (this.dataSourceId && !this.loadingSchema) {
          this.loadSchemaData(this.dataSourceId);
        }
      }, 5000);
    }
  }
  
  ngOnChanges(changes: SimpleChanges): void {
    // Reset component when inlineData changes
    if (changes['inlineData'] && !changes['inlineData'].firstChange) {
      const currentData = changes['inlineData'].currentValue as DataSourceDialogData;
      const previousData = changes['inlineData'].previousValue as DataSourceDialogData;
      
      // Check if the data source ID changed (including transitions to/from null/undefined)
      const currentDataSourceId = currentData?.dataSourceData?.dataSourceId;
      const previousDataSourceId = previousData?.dataSourceData?.dataSourceId;
      
      // Check if mode changed
      const currentMode = currentData?.mode;
      const previousMode = previousData?.mode;
      
      // Detect changes: ID change, mode change, OR dataSourceData presence changed
      const dataSourceIdChanged = currentDataSourceId !== previousDataSourceId;
      const modeChanged = currentMode !== previousMode;
      const dataSourceDataPresenceChanged = (!!currentData?.dataSourceData) !== (!!previousData?.dataSourceData);
      
      // Always reset if there's any meaningful change
      if (dataSourceIdChanged || modeChanged || dataSourceDataPresenceChanged) {
        this.resetComponent();
        this.initializeComponent();
      }
    }
  }
  
  private resetComponent(): void {
    // Reset all component state
    this.currentStage = 'connection-type';
    this.formData = {
      connectionType: '',
      name: '',
      description: '',
      tags: '',
      contentType: 'CSV'
    };
    this.selectedFile = null;
    this.fileInfo = {
      size: '',
      rowCount: '',
      columnCount: ''
    };
    this.isCreatingApiListener = false;
    this.apiListenerError = null;
    this.isRegeneratingToken = false;
    this.tokenRegenerationError = null;
    this.tokenJustCopied = false;
    this.isUploadingFile = false;
    this.fileUploadError = null;
    this.stopSchemaPolling();
  }
  
  private initializeComponent(): void {
    // Set initial stage based on dialog data
    if (this.effectiveData.initialStage) {
      this.currentStage = this.effectiveData.initialStage;
      
      // If starting at file-upload stage, set connection type to excel
      if (this.currentStage === 'file-upload') {
        this.formData.connectionType = 'excel';
      }
    }
    
    // Handle preloaded file
    if (this.effectiveData.preloadedFile) {
      this.selectedFile = this.effectiveData.preloadedFile;
      this.formData.file = this.selectedFile;
      
      // Auto-populate name field with filename (without extension)
      if (!this.formData.name || this.formData.name.trim() === '') {
        this.formData.name = this.selectedFile.name.replace(/\.[^/.]+$/, '');
      }
      
      // Analyze the file
      this.analyzeFile(this.selectedFile);
    }
    
    // If in edit mode, populate form data
    if (this.effectiveData.mode === 'edit' && this.effectiveData.dataSourceData) {
      this.populateFormForEdit();
    }
    
    // Emit initial title for inline mode
    if (this.isInlineMode) {
      this.emitTitle();
    }
  }
  
  private emitTitle(): void {
    if (this.isInlineMode) {
      this.titleChange.emit(this.getDialogTitle());
      this.subtitleChange.emit(''); // Data source dialog doesn't use subtitle
    }
  }

  private populateFormForEdit(): void {
    if (this.effectiveData.dataSourceData) {
      // Map the data source type to connection type
      const dataSource = this.effectiveData.dataSourceData;
      this.formData.connectionType = this.getConnectionTypeFromDataSource(dataSource);
      this.formData.name = dataSource.dataSourceName || '';
      this.formData.description = dataSource.description || '';
      
      // For API Listeners, reconstruct the endpoint and extract token
      if (dataSource.dataSourceTypeId === 1 && dataSource.dataSourceId) {
        // Store the data source ID
        this.dataSourceId = dataSource.dataSourceId;
        
        // Reconstruct the endpoint
        const baseUrl = this.dataService.getBaseUrl();
        this.formData.endpoint = `${baseUrl}/Datasource/apilistener/storeschema/${dataSource.dataSourceId}`;
        
        // Extract token from dataSourceAccessJson if available
        if (dataSource.dataSourceAccessJson) {
          try {
            const accessData = JSON.parse(dataSource.dataSourceAccessJson);
            if (accessData.token || accessData.dataSourceToken) {
              this.formData.token = accessData.token || accessData.dataSourceToken;
            }
          } catch (error) {
            console.warn('Failed to parse dataSourceAccessJson:', error);
            // If parsing fails, the token might be stored directly as a string
            this.formData.token = dataSource.dataSourceAccessJson;
          }
        }
        
        // Load schema data if we have the schemaJson
        if (dataSource.schemaJson) {
          this.parseSchemaJson(dataSource.schemaJson);
        }
        
        // Start polling if schema is not yet valid (after a brief delay to let UI settle)
        setTimeout(() => {
          this.startSchemaPolling();
        }, 100);
      }
      
      // Add other mappings as needed for different data source types
    }
  }

  private getConnectionTypeFromDataSource(dataSource: any): string {
    // Map data source type ID to connection type
    switch (dataSource.dataSourceTypeId) {
      case 1: return 'api-listener';
      case 2: return 'api-retriever';
      // Add other mappings as needed
      default: return 'csv'; // Default fallback
    }
  }

  onConnectionTypeSelect(connectionType: any): void {
    this.formData.connectionType = connectionType.id;
    this.emitTitle();
    
    // Immediately navigate to the appropriate next stage
    if (connectionType.type === 'File') {
      this.currentStage = 'file-upload';
    } else if (connectionType.id === 'api-listener') {
      this.currentStage = 'api-setup';
    } else if (connectionType.id === 'api-retriever') {
      this.currentStage = 'api-setup';
    }
    
    this.emitTitle(); // Update title after stage change
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFile = input.files[0];
      this.formData.file = this.selectedFile;
      
      // Auto-populate name field with filename (without extension) if name is empty
      if (!this.formData.name || this.formData.name.trim() === '') {
        this.formData.name = this.selectedFile.name.replace(/\.[^/.]+$/, '');
      }
      
      // Simulate file analysis
      this.analyzeFile(this.selectedFile);
      
      // Clear any previous errors
      this.fileUploadError = null;
    }
  }

  browseFiles(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = this.getFileAcceptTypes();
    input.onchange = (event) => this.onFileSelected(event);
    input.click();
  }

  getFileAcceptTypes(): string {
    switch (this.formData.connectionType) {
      case 'excel': return '.xlsx,.xls,.csv,.json';
      case 'csv': return '.csv,.txt';
      case 'parquet': return '.parquet';
      case 'json': return '.json';
      default: return '*';
    }
  }

  analyzeFile(file: File): void {
    // Simulate file analysis
    this.fileInfo = {
      size: `${(file.size / 1024).toFixed(0)}kb`,
      rowCount: '##',
      columnCount: '##'
    };
  }

  async onUploadFile(): Promise<void> {
    // Validate required fields
    if (!this.selectedFile) {
      this.fileUploadError = 'Please select a file to upload';
      return;
    }

    if (!this.formData.name || this.formData.name.trim() === '') {
      this.fileUploadError = 'Data Source Name is required';
      return;
    }

    this.isUploadingFile = true;
    this.fileUploadError = null;

    // Check if datasource name already exists
    this.dataService.checkDataSourceExists(this.formData.name?.trim() || '').subscribe({
      next: async (existsResult) => {
        if (existsResult.exists) {
          this.fileUploadError = 'Data source name already exists';
          this.isUploadingFile = false;
          return;
        }
        
        // Validate that selectedFile is not null
        if (!this.selectedFile) {
          this.fileUploadError = 'No file selected';
          this.isUploadingFile = false;
          return;
        }
        
        try {
          // Determine data source name
          const dataSourceName = this.formData.name?.trim() || this.selectedFile.name;
          const tags = this.formData.tags?.trim() || undefined;

          // Determine data source type ID based on file extension
          const fileExtension = this.selectedFile.name.split('.').pop()?.toLowerCase() || '';

          // Reset progress tracking
          this.uploadProgress = 0;
          this.uploadedBytes = 0;
          this.totalBytes = this.selectedFile.size;
          this.uploadStatusText = 'Uploading file...';

          // Upload the file with progress tracking (no base64 encoding needed)
          this.dataService.uploadFileDataSourceWithProgress(this.selectedFile, dataSourceName, tags).subscribe({
            next: (event: any) => {
              if (event.type === HttpEventType.UploadProgress) {
                // Track upload progress
                if (event.total) {
                  this.totalBytes = event.total;
                  this.uploadedBytes = event.loaded;
                  this.uploadProgress = Math.round((100 * event.loaded) / event.total);
                }
              } else if (event.type === HttpEventType.Response) {
                // Upload complete, switch to indeterminate while waiting for response
                this.uploadProgress = 100;
                this.uploadStatusText = 'Processing...';
                
                // API returns full response object (as text due to responseType: 'text')
                // Need to parse JSON string first
                const response = event.body;
                console.log('📥 Upload response from API:', response, 'Type:', typeof response);
                
                let dataSourceId: string;
                if (typeof response === 'string') {
                  // Check if it's a JSON string
                  if (response.trim().startsWith('{') || response.trim().startsWith('[')) {
                    try {
                      const parsed = JSON.parse(response);
                      dataSourceId = parsed.dataSourceId;
                      console.log('✅ Parsed JSON response and extracted dataSourceId:', dataSourceId);
                    } catch (e) {
                      console.error('❌ Failed to parse response as JSON:', e);
                      dataSourceId = response; // Fallback to raw response
                    }
                  } else {
                    // It's a plain GUID string
                    dataSourceId = response;
                  }
                } else {
                  // It's already an object
                  dataSourceId = response.dataSourceId;
                }
                
                console.log('📤 Final dataSourceId to use:', dataSourceId);
                
                // If projectId is available, assign the data source to the project
                if (this.effectiveData.projectId) {
                  this.dataService.assignDataSourceToProject(this.effectiveData.projectId, dataSourceId).subscribe({
                    next: (success) => {
                      this.isUploadingFile = false;
                      if (success) {
                        console.log('Data source assigned to project successfully');
                        this.completeFileUpload(dataSourceId, dataSourceName);
                      } else {
                        this.fileUploadError = 'File uploaded but failed to assign to project';
                      }
                    },
                    error: (error) => {
                      this.isUploadingFile = false;
                      // Don't display error message for 402 Payment Required - already handled by interceptor
                      if (error.status !== 402) {
                        this.fileUploadError = 'File uploaded but failed to assign to project: ' + this.extractErrorMessage(error);
                      }
                      console.error('Error assigning data source to project:', error);
                    }
                  });
                } else {
                  // No projectId, complete without assignment
                  this.isUploadingFile = false;
                  this.completeFileUpload(dataSourceId, dataSourceName);
                }
              }
            },
            error: (error) => {
              // Don't display error message for 402 Payment Required - already handled by interceptor
              if (error.status !== 402) {
                // Extract error message - the error object from the service wraps the HttpErrorResponse
                this.fileUploadError = this.extractErrorMessage(error);
              }
              this.isUploadingFile = false;
              console.error('Error uploading file:', error);
            }
          });
      } catch (error: any) {
        this.fileUploadError = error.message || 'Failed to upload file';
        this.isUploadingFile = false;
        console.error('Error processing file:', error);
      }
      },
      error: (error) => {
        // Don't display error message for 402 Payment Required - already handled by interceptor
        if (error.status !== 402) {
          this.fileUploadError = 'Failed to check data source name: ' + this.extractErrorMessage(error);
        }
        this.isUploadingFile = false;
        console.error('Error checking data source existence:', error);
      }
    });
  }

  private getDataSourceTypeIdFromExtension(extension: string): number {
    // All file uploads use DataSourceTypeId = 3 (Upload Files)
    // The API expects this specific ID for the uploadfiles endpoint
    return 3; // Upload Files type - required by API
  }

  private extractErrorMessage(error: any): string {
    // The error from the service is a wrapped Error object
    // The data service extracts string responses from the API and puts them in error.message
    if (error && error.message) {
      return error.message;
    }
    
    return 'Failed to upload file. Please try again.';
  }

  private completeFileUpload(dataSourceId: string, dataSourceName: string): void {
    // Emit event for parent component
    if (this.isInlineMode) {
      this.dataSourceCreated.emit({
        dataSourceId: dataSourceId,
        dataSourceName: dataSourceName,
        hasSchema: true // File uploads have immediate schema
      });
    }
    
    // Close dialog or emit submit
    const result = {
      data: {
        ...this.formData,
        dataSourceId: dataSourceId,
        name: dataSourceName
      }
    };
    
    if (this.isInlineMode) {
      this.inlineSubmit.emit(result);
    } else if (this.dialogRef) {
      this.dialogRef.close(result);
    }
  }

  onCreateApiListener(): void {
    this.isCreatingApiListener = true;
    this.apiListenerError = null;
    
    // Create the registration payload
    const registrationData: DataSourceRegister = {
      DataSourceName: this.formData.name || 'API Listener',
      DataSourceTypeId: 1, // API Listener type
      ContentTypeId: this.getContentTypeId(this.formData.contentType || 'JSON'),
      Tags: this.formData.tags || 'api-listener'
    };

    // Check if datasource name already exists
    this.dataService.checkDataSourceExists(registrationData.DataSourceName).subscribe({
      next: (existsResult) => {
        if (existsResult.exists) {
          this.apiListenerError = 'Data source name already exists';
          this.isCreatingApiListener = false;
          return;
        }
        
        this.dataService.registerApiListenerDataSource(registrationData).subscribe({
          next: (response) => {
            // Store the real endpoint and token from the API response
            const baseUrl = this.dataService.getBaseUrl();
            this.formData.endpoint = `${baseUrl}/Datasource/apilistener/storeschema/${response.dataSourceId}`;
            this.formData.token = response.dataSourceToken;
            this.dataSourceId = response.dataSourceId;
            this.currentStage = 'api-listening';
            this.isCreatingApiListener = false;
            
            // Emit event for parent component to update the data source card
            if (this.isInlineMode) {
              this.dataSourceCreated.emit({
                dataSourceId: response.dataSourceId,
                dataSourceName: this.formData.name || 'API Listener',
                hasSchema: false
              });
            }
            
            // Use setTimeout to avoid ExpressionChangedAfterItHasBeenCheckedError
            setTimeout(() => {
              this.emitTitle();
            });
            
            // Load schema data for the newly created data source with a slight delay
            setTimeout(() => {
              this.loadSchemaData(response.dataSourceId);
              // Start polling if schema is not yet valid
              setTimeout(() => {
                this.startSchemaPolling();
              }, 100);
            }, 500);
          },
          error: (error) => {
            this.apiListenerError = error.message || 'Failed to create API listener';
            this.isCreatingApiListener = false;
            console.error('Error creating API listener:', error);
          }
        });
      },
      error: (error) => {
        this.apiListenerError = 'Failed to check data source name: ' + this.extractErrorMessage(error);
        this.isCreatingApiListener = false;
        console.error('Error checking data source existence:', error);
      }
    });
  }

  private getContentTypeId(contentType: string): number {
    // Map content types to IDs
    switch (contentType.toLowerCase()) {
      case 'json': return 1;
      case 'csv': return 2;
      case 'xml': return 3;
      default: return 1; // Default to JSON
    }
  }

  copyToClipboard(text: string, isCode: boolean = false): void {
    navigator.clipboard.writeText(text).then(() => {
      if (isCode) {
        this.codeJustCopied = true;
        setTimeout(() => {
          this.codeJustCopied = false;
        }, 2000);
      } else {
        this.tokenJustCopied = true;
        setTimeout(() => {
          this.tokenJustCopied = false;
        }, 2000);
      }
    }).catch(err => {
      console.error('Failed to copy to clipboard:', err);
    });
  }

  selectCurlText(event: MouseEvent): void {
    const preElement = event.target as HTMLPreElement;
    const range = document.createRange();
    range.selectNodeContents(preElement);
    const selection = window.getSelection();
    if (selection) {
      selection.removeAllRanges();
      selection.addRange(range);
    }
  }

  getTokenButtonText(): string {
    if (this.isRegeneratingToken) {
      return 'Regenerating...';
    }
    if (!this.formData.token) {
      return 'Regenerate';
    }
    if (this.tokenJustCopied) {
      return 'Copied!';
    }
    return 'Copy';
  }

  regenerateToken(): void {
    // Use dataSourceId from component state (set during create or edit)
    const dataSourceId = this.dataSourceId || this.effectiveData.dataSourceData?.dataSourceId;
    
    if (!dataSourceId) {
      this.tokenRegenerationError = 'No data source ID available for token regeneration';
      console.error('No data source ID available for token regeneration');
      return;
    }

    this.isRegeneratingToken = true;
    this.tokenRegenerationError = null;

    this.dataService.regenerateApiListenerToken(dataSourceId).subscribe({
      next: (newToken) => {
        this.formData.token = newToken;
        this.isRegeneratingToken = false;
        console.log('Token regenerated successfully:', newToken);
        
        // Automatically copy the new token to clipboard
        this.copyToClipboard(newToken);
        
        // Show "Copied!" for 2 seconds, then back to "Copy"
        this.showCopyConfirmation();
      },
      error: (error) => {
        this.isRegeneratingToken = false;
        this.tokenRegenerationError = error.message || 'Failed to regenerate token';
        console.error('Error regenerating token:', error);
      }
    });
  }

  private showCopyConfirmation(): void {
    this.tokenJustCopied = true;
    setTimeout(() => {
      this.tokenJustCopied = false;
    }, 2000); // Show "Copied!" for 2 seconds
  }

  goBack(): void {
    // Prevent navigation away from api-listening stage
    if (this.currentStage === 'api-listening') {
      return;
    }
    
    if (this.currentStage === 'file-upload' || this.currentStage === 'api-setup') {
      this.currentStage = 'connection-type';
    }
    this.emitTitle();
  }

  canProceed(): boolean {
    switch (this.currentStage) {
      case 'connection-type':
        return !!this.formData.connectionType;
      case 'file-upload':
        return !!this.selectedFile && !!this.formData.name && this.formData.name.trim() !== '';
      case 'api-setup':
        return !!this.formData.name && !!this.formData.contentType;
      case 'api-listening':
        return true;
      default:
        return false;
    }
  }

  onSubmit(): void {
    if (this.currentStage === 'connection-type') {
      // Progress to next stage based on selected connection type
      const selectedType = this.connectionTypes.find(type => type.id === this.formData.connectionType);
      if (selectedType?.type === 'File') {
        this.currentStage = 'file-upload';
      } else if (selectedType?.id === 'api-listener') {
        this.currentStage = 'api-setup';
      }
      return;
    }

    if (this.currentStage === 'api-setup') {
      this.onCreateApiListener();
      return;
    }

    if (this.currentStage === 'file-upload') {
      this.onUploadFile();
      return;
    }

    if (this.canProceed()) {
      if (this.isInlineMode) {
        this.inlineSubmit.emit({ data: this.formData });
      } else if (this.dialogRef) {
        this.dialogRef.close({ data: this.formData });
      }
    }
  }

  loadExistingDataSources(): void {
    this.loadingDataSources = true;
    this.dataService.getDataSourceList().subscribe({
      next: (dataSources) => {
        this.existingDataSources = dataSources;
        this.filteredDataSources = dataSources;
        this.loadingDataSources = false;
      },
      error: (error) => {
        console.error('Error loading existing data sources:', error);
        this.loadingDataSources = false;
      }
    });
  }

  filterDataSources(): void {
    const query = this.searchQuery.toLowerCase().trim();
    if (!query) {
      this.filteredDataSources = this.existingDataSources;
    } else {
      this.filteredDataSources = this.existingDataSources.filter(ds =>
        ds.dataSourceName.toLowerCase().includes(query) ||
        ds.tags?.toLowerCase().includes(query)
      );
    }
  }

  selectExistingDataSource(dataSource: any): void {
    this.selectedExistingDataSource = dataSource;
    
    // If projectId is available, assign the data source to the project
    if (this.effectiveData.projectId) {
      this.isUploadingFile = true; // Reuse the loading state
      this.fileUploadError = null;
      
      this.dataService.assignDataSourceToProject(this.effectiveData.projectId, dataSource.dataSourceId).subscribe({
        next: (success) => {
          this.isUploadingFile = false;
          if (success) {
            console.log('Data source assigned to project successfully');
            
            // Emit the selection with the dataSourceId
            const result = {
              data: {
                ...this.formData,
                existingDataSourceId: dataSource.dataSourceId,
                dataSourceId: dataSource.dataSourceId,
                name: dataSource.dataSourceName
              }
            };
            
            if (this.isInlineMode) {
              this.inlineSubmit.emit(result);
            } else if (this.dialogRef) {
              this.dialogRef.close(result);
            }
          } else {
            this.fileUploadError = 'Failed to assign data source to project';
          }
        },
        error: (error) => {
          this.isUploadingFile = false;
          this.fileUploadError = this.extractErrorMessage(error);
          console.error('Error assigning data source to project:', error);
        }
      });
    } else {
      // No projectId, just emit the selection
      const result = {
        data: {
          ...this.formData,
          existingDataSourceId: dataSource.dataSourceId,
          dataSourceId: dataSource.dataSourceId, // Include dataSourceId for auto-selection to work
          name: dataSource.dataSourceName
        }
      };
      
      if (this.isInlineMode) {
        this.inlineSubmit.emit(result);
      } else if (this.dialogRef) {
        this.dialogRef.close(result);
      }
    }
  }

  formatTags(tags: string | null | undefined): string[] {
    if (!tags) {
      return [];
    }
    
    // Split by comma and clean up whitespace
    return tags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0);
  }

  onCancel(): void {
    if (this.isInlineMode) {
      this.inlineCancel.emit();
    } else if (this.dialogRef) {
      this.dialogRef.close();
    }
  }

  onFinishApiListenerSetup(): void {
    // Stop polling when finishing setup
    this.stopSchemaPolling();
    
    if (this.isInlineMode) {
      // Emit dataSourceCreated event with the setup completion details
      this.dataSourceCreated.emit({
        dataSourceId: this.dataSourceId || '',
        dataSourceName: this.formData.name || '',
        hasSchema: this.isApiListenerFullySetup()
      });
    } else if (this.dialogRef) {
      // Close the dialog with success result in the format expected by consumers
      // Dashboard expects result.data.dataSourceId and result.data.name
      this.dialogRef.close({
        success: true,
        data: {
          dataSourceId: this.dataSourceId,
          dataSourceName: this.formData.name,
          name: this.formData.name,
          hasSchema: this.isApiListenerFullySetup()
        }
      });
    }
  }

  getDialogTitle(): string {
    switch (this.currentStage) {
      case 'connection-type':
        return 'New Data Source';
      case 'file-upload':
        return this.getConnectionTypeLabel();
      case 'api-setup':
        return 'Setup API Listener';
      case 'api-listening':
        return 'API Listener Ready';
      default:
        return 'New Data Source';
    }
  }

  getConnectionTypeLabel(): string {
    const type = this.connectionTypes.find(t => t.id === this.formData.connectionType);
    return type ? type.label : '';
  }

  getSubmitButtonText(): string {
    if (this.isCreatingApiListener) {
      return 'Creating...';
    }
    
    if (this.isUploadingFile) {
      return 'Uploading...';
    }
    
    switch (this.currentStage) {
      case 'file-upload':
        return 'Upload';
      case 'api-setup':
        return this.formData.connectionType === 'api-listener' ? 'Create + Configure' : 'Create';
      case 'api-listening':
        return 'Finish';
      default:
        return 'Next';
    }
  }

  /**
   * Returns the appropriate placeholder based on the content type.
   * This method can be reused across different code sample generators (cURL, C#, etc.)
   */
  private getDataPlaceholder(): string {
    // API Listener only supports JSON
    return 'your_json_here';
  }

  getCurlCommand(): string {
    if (!this.formData.endpoint) {
      return '# Endpoint not available';
    }
    
    if (!this.formData.token) {
      return '# Token not available. Please regenerate the token first.';
    }
    
    return `curl -X POST "${this.formData.endpoint}" \\
  -H "Authorization: Bearer ${this.formData.token}" \\
  -H "Content-Type: application/json" \\
  -d '/* your data here */'`;
  }

  getCSharpCode(): string {
    if (!this.formData.endpoint) {
      return '// Endpoint not available';
    }
    
    if (!this.formData.token) {
      return '// Token not available. Please regenerate the token first.';
    }
    
    return `using System;
using System.Net.Http;
using System.Text;
using System.Text.Json;
using System.Threading.Tasks;

class ApiClient
{
    private static readonly string ApiEndpoint = "${this.formData.endpoint}";
    private static readonly string BearerToken = "${this.formData.token}";
    
    public static async Task<string> SendDataAsync(object data)
    {
        using var client = new HttpClient();
        client.DefaultRequestHeaders.Add("Authorization", $"Bearer {BearerToken}");
        
        var json = JsonSerializer.Serialize(data);
        var content = new StringContent(json, Encoding.UTF8, "application/json");
        var response = await client.PostAsync(ApiEndpoint, content);
        
        response.EnsureSuccessStatusCode();
        return await response.Content.ReadAsStringAsync();
    }
    
    static async Task Main(string[] args)
    {
        // Example usage - send your JSON object directly
        object myData = /* your data here */;
        var result = await SendDataAsync(myData);
        Console.WriteLine(result);
    }
}`;
  }

  getVBNetCode(): string {
    if (!this.formData.endpoint) {
      return '\'Endpoint not available';
    }
    
    if (!this.formData.token) {
      return '\'Token not available. Please regenerate the token first.';
    }
    
    return `Imports System
Imports System.Net.Http
Imports System.Text
Imports System.Text.Json
Imports System.Threading.Tasks

Module ApiClient
    Private Const ApiEndpoint As String = "${this.formData.endpoint}"
    Private Const BearerToken As String = "${this.formData.token}"
    
    Public Async Function SendDataAsync(data As Object) As Task(Of String)
        Using client As New HttpClient()
            client.DefaultRequestHeaders.Add("Authorization", $"Bearer {BearerToken}")
            
            Dim json As String = JsonSerializer.Serialize(data)
            Dim content As New StringContent(json, Encoding.UTF8, "application/json")
            Dim response = Await client.PostAsync(ApiEndpoint, content)
            
            response.EnsureSuccessStatusCode()
            Return Await response.Content.ReadAsStringAsync()
        End Using
    End Function
    
    Async Function Main() As Task
        ' Example usage - send your JSON object directly
        Dim myData As Object = Nothing ' your data here
        Dim result = Await SendDataAsync(myData)
        Console.WriteLine(result)
    End Function
End Module`;
  }

  getPythonCode(): string {
    if (!this.formData.endpoint) {
      return '# Endpoint not available';
    }
    
    if (!this.formData.token) {
      return '# Token not available. Please regenerate the token first.';
    }
    
    return `import requests
import json
from typing import Any, Dict

API_ENDPOINT = "${this.formData.endpoint}"
BEARER_TOKEN = "${this.formData.token}"

def send_data(data: Any) -> Dict:
    """
    Send data to the API endpoint.
    
    Args:
        data: The data to send (JSON object/dict)
    
    Returns:
        The JSON response from the API
    """
    headers = {
        "Authorization": f"Bearer {BEARER_TOKEN}",
        "Content-Type": "application/json"
    }
    
    response = requests.post(API_ENDPOINT, headers=headers, json=data)
    response.raise_for_status()
    return response.json()

if __name__ == "__main__":
    # Example usage - send your JSON object directly
    my_data = {}  # your data here
    result = send_data(my_data)
    print(result)`;
  }

  getJavaCode(): string {
    if (!this.formData.endpoint) {
      return '// Endpoint not available';
    }
    
    if (!this.formData.token) {
      return '// Token not available. Please regenerate the token first.';
    }
    
    return `import java.net.URI;
import java.net.http.HttpClient;
import java.net.http.HttpRequest;
import java.net.http.HttpResponse;
import com.google.gson.Gson;
import java.util.Map;

public class ApiClient {
    private static final String API_ENDPOINT = "${this.formData.endpoint}";
    private static final String BEARER_TOKEN = "${this.formData.token}";
    private static final HttpClient client = HttpClient.newHttpClient();
    private static final Gson gson = new Gson();
    
    public static String sendData(Object data) throws Exception {
        String json = gson.toJson(data);
        
        HttpRequest request = HttpRequest.newBuilder()
            .uri(URI.create(API_ENDPOINT))
            .header("Authorization", "Bearer " + BEARER_TOKEN)
            .header("Content-Type", "application/json")
            .POST(HttpRequest.BodyPublishers.ofString(json))
            .build();
        
        HttpResponse<String> response = client.send(request, 
            HttpResponse.BodyHandlers.ofString());
        
        if (response.statusCode() != 200) {
            throw new RuntimeException("API returned status: " + response.statusCode());
        }
        
        return response.body();
    }
    
    public static void main(String[] args) throws Exception {
        // Example usage - send your JSON object directly
        Object myData = /* your data here */;
        String result = sendData(myData);
        System.out.println(result);
    }
}`;
  }

  loadSchemaData(dataSourceId: string): void {
    // Don't start a new request if one is already in progress
    if (this.loadingSchema) {
      console.log('Schema load already in progress, skipping duplicate request');
      return;
    }
    
    this.loadingSchema = true;
    console.log('Loading schema for data source:', dataSourceId);
    
    // Track if schema was present before this load
    const hadSchemaBefore = this.isApiListenerFullySetup();
    
    this.dataService.getDataSource(dataSourceId).subscribe({
      next: (dataSource) => {
        console.log('Received data source:', dataSource);
        console.log('Schema JSON:', dataSource.schemaJson);
        
        if (dataSource.schemaJson) {
          this.parseSchemaJson(dataSource.schemaJson);
        } else {
          console.log('No schemaJson in data source');
          this.schemaColumns = [];
        }
        this.loadingSchema = false;
        
        // Update formData.schemaJson for validation check
        this.formData.schemaJson = dataSource.schemaJson;
        
        // Check if schema just became available
        const hasSchemaNow = this.isApiListenerFullySetup();
        
        // If schema was just detected (transition from no schema to having schema)
        if (!hadSchemaBefore && hasSchemaNow && this.isInlineMode) {
          console.log('Schema detected! Emitting schemaDetected event');
          this.schemaDetected.emit({ dataSourceId: dataSourceId });
        }
        
        // Stop polling if schema is now valid
        if (hasSchemaNow) {
          this.stopSchemaPolling();
        }
      },
      error: (error) => {
        console.error('Error loading schema data:', error);
        this.loadingSchema = false;
        this.schemaColumns = [];
      }
    });
  }

  private parseSchemaJson(schemaJson: string): void {
    try {
      // Parse the schema JSON (may be double-encoded)
      let schema = JSON.parse(schemaJson);
      console.log('First parse result:', schema, 'Type:', typeof schema);
      
      // If the result is a string, parse again (double-encoded JSON)
      if (typeof schema === 'string') {
        schema = JSON.parse(schema);
        console.log('Second parse result:', schema);
      }
      
      // Handle new schema format: [{column, atomic_dtype, soft_dtype, stats}, ...]
      if (Array.isArray(schema) && schema.length > 0 && schema[0].column) {
        this.schemaColumns = schema.map((col: any) => ({
          ColumnName: col.column,
          DataType: col.atomic_dtype || 'string'
        }));
        console.log('Set schemaColumns from new format:', this.schemaColumns);
      }
      // Handle old array format
      else if (Array.isArray(schema)) {
        this.schemaColumns = schema;
        console.log('Set schemaColumns to array:', this.schemaColumns);
      } else if (schema.columns && Array.isArray(schema.columns)) {
        this.schemaColumns = schema.columns;
        console.log('Set schemaColumns from schema.columns:', this.schemaColumns);
      } else {
        this.schemaColumns = [];
        console.log('Schema format not recognized, cleared schemaColumns');
      }
    } catch (error) {
      console.error('Error parsing schema JSON:', error);
      this.schemaColumns = [];
    }
  }

  isApiListenerFullySetup(): boolean {
    // Check if we have valid schema data (API Listener is fully set up)
    // First check formData.schemaJson (set during polling in loadSchemaData)
    if (this.formData.schemaJson) {
      const schemaJson = this.formData.schemaJson;
      return schemaJson.trim() !== '' && schemaJson !== 'null';
    }
    
    // Fallback to effectiveData for edit mode
    if (this.effectiveData.dataSourceData?.schemaJson) {
      const schemaJson = this.effectiveData.dataSourceData.schemaJson;
      return schemaJson.trim() !== '' && schemaJson !== 'null';
    }
    
    return false;
  }

  getFileSizeString(bytes: number): string {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }
}
